import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';
import Bowser from 'bowser';

import ScrollSequence from './ScrollSequence';

class BrixSffCampaign {
  static init(...parameters) {
    return new BrixSffCampaign(...parameters);
  }

  constructor(container) {
    document.body.classList.add('-hidden-menu');

    // Get device type
    const parser = Bowser.getParser(window.navigator.userAgent);
    const platform = parser.getPlatform().type;
    const os = parser.getOS().name;
    const isTouchDevice = platform === 'mobile' || platform === 'tablet' || (os === 'macOS' && 'ontouchend' in document);

    gsap.registerPlugin(ScrollTrigger);

    // Smooth content
    let smoother = null;
    if (!isTouchDevice) {
      gsap.registerPlugin(ScrollSmoother);
      const smoothContent = document.querySelector('.smooth-content');
      smoothContent.classList.add('-enabled');
      smoother = ScrollSmoother.create({
        wrapper: '.smooth-wrapper',
        content: smoothContent,
        smooth: 1,
        effects: true,
      });
    }

    ScrollTrigger.config({ limitCallbacks: true, ignoreMobileResize: true });

    const headerScrollButton = container.querySelector('.header .scroll-indicator-button');
    const intro = container.querySelector('.intro');
    headerScrollButton.addEventListener('click', () => {
      if (!isTouchDevice) {
        smoother.scrollTo(intro, true);
      } else {
        intro.scrollIntoView({ behavior: 'smooth' });
      }
    });

    ScrollTrigger.create({
      trigger: headerScrollButton,
      toggleClass: '-active',
    });

    const headerVideo = container.querySelector('.header .video');

    ScrollTrigger.create({
      trigger: headerVideo,
      onToggle: ({ isActive }) => {
        if (isActive) {
          headerVideo.play();
        } else {
          headerVideo.pause();
        }
      },
    });

    const introScrollButton = container.querySelector('.intro .scroll-indicator-button');
    const flexiBack = container.querySelector('.flexi-back');
    introScrollButton.addEventListener('click', () => {
      if (!isTouchDevice) {
        gsap.to(smoother, {
          scrollTop: smoother.offset(flexiBack),
          duration: 5,
        });
      } else {
        flexiBack.scrollIntoView({ behavior: 'smooth' });
      }
    });

    ScrollTrigger.create({
      trigger: introScrollButton,
      toggleClass: '-active',
    });

    const introSequenceWrapper = container.querySelector('.intro-sequence .sequence-wrapper');
    const introSequence = new ScrollSequence(introSequenceWrapper, 181, true, true, 'top top', 'bottom+=800% top');
    introSequence.init();

    gsap.to('.intro-sequence .heading', {
      opacity: 1,
      y: 0,
      scrollTrigger: {
        trigger: introSequenceWrapper,
        start: 'bottom top',
        end: 'bottom top-=500px',
        scrub: 0.2,
      },
    });

    const features = container.querySelectorAll('.feature');
    [...features].forEach((feature) => {
      const featureIntro = feature.querySelector('.intro');

      if (featureIntro) {
        const video = featureIntro.querySelector('.video');
        const heading = featureIntro.querySelector('.heading');
        let isHeadingVisible = false;

        ScrollTrigger.create({
          trigger: video,
          start: 'top bottom',
          end: `bottom+=${window.innerHeight} top`,
          onToggle: ({ isActive }) => {
            if (isActive) {
              video.play();
            } else {
              video.pause();
            }
          },
          onUpdate: ({ progress }) => {
            if (progress === 0) {
              gsap.set(heading, {
                opacity: 0,
                y: 30,
              });
              isHeadingVisible = false;
            }

            if (!isHeadingVisible && progress > 0.2) {
              gsap.to(heading, {
                opacity: 1,
                y: 0,
                duration: 2,
                onComplete: () => {
                  isHeadingVisible = true;
                },
              });
            }
          },
        });
      }
    });

    const gridItems = container.querySelectorAll('.grid-item');
    [...gridItems].forEach((gridItem) => {
      const videos = gridItem.querySelectorAll('.video');
      const images = gridItem.querySelectorAll('.image');

      if (videos && videos.length) {
        [...videos].forEach((video) => {
          ScrollTrigger.create({
            trigger: gridItem,
            onToggle: ({ isActive }) => {
              if (isActive) {
                video.play();
              } else {
                video.pause();
              }
            },
          });
        });
      }

      if (images && images.length) {
        [...images].forEach((image) => {
          this.updateImageSrc(image);
        });
      }
    });

    const seatImage = container.querySelector('.seat-image');
    this.updateImageSrc(seatImage);

    const joiTiltWrapper = container.querySelector('.joi-tilt');
    const joiTilt = joiTiltWrapper.querySelector('.image:nth-child(2)');
    const joiTiltAmountElement = joiTiltWrapper.querySelector('.tilt-amount');

    let tiltAmount = 0;

    const tiltTimeLine = gsap.timeline({
      repeat: 1,
      yoyo: true,
      repeatDelay: 0.3,
      scrollTrigger: {
        trigger: joiTiltWrapper,
        start: '25% center',
        end: '70% center',
        scrub: true,
        onUpdate: () => {
          const progress = tiltTimeLine.progress();
          tiltAmount = progress * 17;
          joiTiltAmountElement.innerHTML = `${Math.floor(tiltAmount)}°`;
        },
      },
    });

    tiltTimeLine
      .to(joiTilt, { rotation: -17 })
      .to('.border-left', { rotation: -17 }, '<')
      .to('.slice', { clipPath: 'polygon(50% -3%, 50.00% 53.00%, 33.75% -0.10%)' }, '<');

    const disassemblySequenceWrapper = container.querySelector('.disassembly-sequence .sequence-wrapper');
    const disassemblySequence = new ScrollSequence(disassemblySequenceWrapper, 136, true, true, 'top top', 'bottom+=300% top');
    disassemblySequence.init();

    const fabricSequenceWrapper = container.querySelector('.fabric-sequence-inner');
    const fabricSequence = new ScrollSequence(
      fabricSequenceWrapper, 28, false, false, 'top+=25% bottom', 'top-=25% top+=25%',
    );
    fabricSequence.init();

    const isolatedChairImage = container.querySelector('.isolated-chair');
    this.updateImageSrc(isolatedChairImage);
  }

  updateImageSrc(image) {
    const newSRC = this.getImagePath(image.dataset.src);
    const newImage = document.createElement('img');
    const loadImage = () => {
      newImage.onload = () => {
        newImage.onload = null; // Avoid recursion
        newImage.src = image.src; // Swap the src
        image.src = newSRC; // eslint-disable-line no-param-reassign

        // Place the low-res version on top and then fade it out.
        gsap.set(newImage, {
          position: 'absolute',
          top: image.offsetTop,
          left: image.offsetLeft,
          width: image.offsetWidth,
          height: image.offsetHeight,
        });

        image.parentNode.appendChild(newImage);

        gsap.to(newImage, {
          opacity: 0,
          onComplete: () => newImage.parentNode.removeChild(newImage),
        });

        if (st) st.kill(); // eslint-disable-line no-use-before-define
      };
      newImage.src = newSRC;
    };

    let st = ScrollTrigger.create({
      trigger: image,
      start: '-100% bottom',
      onEnter: loadImage,
      onEnterBack: loadImage, // Make sure it works in either direction
    });
  }

  getImagePath(src) {
    const image = src.split('.');
    let newSrc = '';

    // else if (window.innerWidth < 1200) {
    //   newSrc = `${image[0]}_m.${image[1]}`;
    // }
    if (window.innerWidth < 768) {
      newSrc = `${image[0]}_s.${image[1]}`;
    } else {
      newSrc = `${image[0]}_l.${image[1]}`;
    }

    return newSrc;
  }
}

// Register class
if (window.brix && window.brix.registerBlock) {
  window.brix.registerBlock('brix-sff-campaign', BrixSffCampaign.init);
}
